import { Box, Grid, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useContext, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';
import { NameRegex, EmailRegex } from '../../../../configs/RegexConfig';
import { UpdateUserRequest } from '../../../../models/UsersModels';
import useModal from '../../../../hooks/useModal/useModal';

import PageTitle from '../../../../components/Text/PageTitle';
import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import TextInput from '../../../../components/TextInput/TextInput';
import UserDetailsOptions from './UserDetailsOptions';

export const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid${theme.palette.disabled.main}`,
  padding: '0.5rem',
  minHeight: '4rem',
  alignItems: 'center',
  fontSize: theme.typography.body1.fontSize,
  lineHeight: 'normal',
  '& .label': {
    color: theme.palette.text.disabled,
  },
}));

interface UserDetailsInfoRowsProps {
  isLoading: boolean;
  updateUserDetails: (d: UpdateUserRequest, setFormOpen: (val: boolean) => void) => void;
  deleteUser: (userId: string) => void;
  assignDevice: () => void;
  addDevice: () => void;
  assignSim?: () => void;
}

function UserDetailsInfoRows({
  isLoading,
  updateUserDetails,
  deleteUser,
  assignDevice,
  addDevice,
  assignSim,
}: UserDetailsInfoRowsProps) {
  const [t] = useTranslation();
  const user = useContext(UserDetailsContext);
  const modal = useModal();
  const [formOpen, setFormOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      companyRole: user?.companyRole || '',
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      firstName: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NameRegex,
          t('common.inputValidations.validField', {
            field: t(`pages.userDetails.firstName`).toLowerCase(),
          }),
        ),
      lastName: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NameRegex,
          t('common.inputValidations.validField', {
            field: t(`pages.userDetails.lastName`).toLowerCase(),
          }),
        ),
      email: yup
        .string()
        .matches(EmailRegex, t('common.inputValidations.email'))
        .required(t('common.inputValidations.requiredField')),
      companyRole: yup
        .string()
        .notRequired()
        .matches(
          NameRegex,
          t('common.inputValidations.validField', {
            field: t(`pages.userDetails.role`).toLowerCase(),
          }),
        ),
    }),
    onSubmit: (values) => {
      if (user) {
        updateUserDetails({ id: user.id, ...values }, setFormOpen);
      }
    },
  });

  /**
   * Displays the confirmation modal
   */
  const showConfirmationModal = () => {
    modal.showModal({
      modalTitle: t('pages.userDetails.deleteUser'),
      modalDescription: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <span>{t('pages.userDetails.deleteUserMessage1')}</span>
          <span>{t('pages.userDetails.deleteUserMessage2')}</span>
        </Box>
      ),
      confirmationModal: true,
      confirmationButtonLabel: t('common.delete'),
      onAccept: (setOpen) => {
        setOpen(false);
        if (user) {
          deleteUser(user.id);
        }
      },
    });
  };

  return (
    <Box>
      <Grid container>
        <Grid item xl={6} md={12} xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <PageTitle data-testid="page-title">
              {user ? `${user?.firstName} ${user?.lastName}` : <Skeleton width="25%" />}
            </PageTitle>
            <UserDetailsOptions
              formOpen={formOpen}
              editUser={() => setFormOpen(true)}
              assignDevice={assignDevice}
              addDevice={addDevice}
              assignSim={assignSim}
              deleteUser={showConfirmationModal}
              isPerformingAction={isLoading}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container sx={{ paddingTop: '1.5rem', marginBottom: '2.5rem' }}>
        {!formOpen ? (
          <Grid item xl={6} md={12} xs={12}>
            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('pages.userDetails.firstName')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {user ? user?.firstName : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('pages.userDetails.lastName')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {user ? user?.lastName : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('pages.userDetails.emailAddress')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {user ? user?.email : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('pages.userDetails.role')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {user ? <span>{user.companyRole ? user.companyRole : 'n.a.'}</span> : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>
          </Grid>
        ) : (
          <Grid item xl={4} md={12} xs={12}>
            <form onSubmit={formik.handleSubmit}>
              <TextInput
                autoComplete="off"
                key="firstName"
                id="firstName"
                name="firstName"
                type="text"
                label={t(`pages.userDetails.firstName`)}
                value={formik.values.firstName}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                feedback={formik.touched.firstName && Boolean(formik.errors.firstName) ? formik.errors.firstName : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.userDetails.firstName`)}
              />
              <TextInput
                autoComplete="off"
                key="lastName"
                id="lastName"
                name="lastName"
                type="text"
                label={t(`pages.userDetails.lastName`)}
                value={formik.values.lastName}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                feedback={formik.touched.lastName && Boolean(formik.errors.lastName) ? formik.errors.lastName : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.userDetails.lastName`)}
              />
              <TextInput
                autoComplete="off"
                key="email"
                id="email"
                name="email"
                type="text"
                label={t(`pages.userDetails.emailAddress`)}
                value={formik.values.email}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                feedback={formik.touched.email && Boolean(formik.errors.email) ? formik.errors.email : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.userDetails.emailAddress`)}
              />
              <TextInput
                autoComplete="off"
                key="companyRole"
                id="companyRole"
                name="companyRole"
                type="text"
                label={t(`pages.userDetails.role`)}
                value={formik.values.companyRole}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.userDetails.role`)}
              />

              <ButtonGroup sx={{ paddingTop: '0.5rem' }}>
                <Button
                  label={t('common.cancel')}
                  color="alternative2Button"
                  type="submit"
                  onClick={() => {
                    setFormOpen(!formOpen);
                    formik.resetForm();
                  }}
                />
                <Button
                  label={t('common.saveChanges')}
                  color="primaryButton"
                  type="submit"
                  disabled={isLoading || !formik.isValid || sessionStorage.getItem('impersonateId') !== null}
                />
              </ButtonGroup>
            </form>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default memo(UserDetailsInfoRows);
